import { useToggle } from "../../hooks";
import { useContext, useEffect, useRef, useState } from "react";
import { Accordion, AccordionContext, Button, Card, Dropdown, Form, OverlayTrigger, Tooltip, useAccordionButton } from "react-bootstrap";
import { Link } from "react-router-dom";
import { updateStoreGroupId, updateStoreGroupField } from "../../redux/slices/Global";
import { getStores } from "../../redux/slices/Stores";
import { decodeToken } from "../../helpers/api/authService";
import { ResponseHasError } from "../../helpers/Functions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { getManageGroupsStoreTree } from "../../redux/slices/ManageGroups";
import { TooltipIcon } from "../../components";
import { getMainColors } from "../../constants/colors";
import OverflowLabel from "../../components/OverflowLabel";
import { StoreGroupTree } from "../../models/dbModels/other/StoreGroupTree";
import { ZIcon } from "components/ZIcon";
import { ZIconType } from "helpers/utility/iconMap";
import classNames from "classnames";

const pharmacyGroupDropdownShowContainerStyle = {
    maxHeight: "400px",
};

const PharmacyGroupDropdown = () => {
    const [isOpen, toggleDropdown] = useToggle();
    const groups = useAppSelector((state: RootState) => state.manageGroups);
    const storeGroupId = useAppSelector((state: RootState) => state.global.storeGroupId);
    const dispatch = useAppDispatch();
    const global = useAppSelector((state: RootState) => state.global);
    const [groupDropdown, setGroupDropdown] = useState("");
    const currentStoreGroupId = useAppSelector((state: RootState) => state.global.storeGroupId);
    const [hoveringToggle, setHoveringToggle] = useState(false);

    useEffect(() => {
        dispatch(getManageGroupsStoreTree()).then((res) => {
            var decoded = decodeToken();
            if (decoded !== null) {
                let groups = decoded.StoreGroups.split("|");
                let rootGroup = decoded.RootStoreGroup;
                if (!storeGroupId || !groups.includes(storeGroupId.toString())) {
                    dispatch(updateStoreGroupId(Number(groups[0])));
                    dispatch(updateStoreGroupField({ key: "storeName", value: res.data[0].name }));
                }
            }
        })
    }, []);

    const handleChange = (e: any, name: string) => {
        setGroupDropdown(e.target.value);
        dispatch(updateStoreGroupId(Number(e.target.value)));
        dispatch(updateStoreGroupField({ key: "storeName", value: name }));
    };

    //Always refresh the stores when we change group
    useEffect(() => {
        dispatch(getStores({ storeGroupIds: [currentStoreGroupId] }));
    }, [currentStoreGroupId])

    
    type AccordionDropdownButtonProps = {
        text: string;
        canDropdown: boolean;
        eventKey: string;
    }

    function AccordionDropdownButton({ text, canDropdown, eventKey }: AccordionDropdownButtonProps) {
        const { activeEventKey } = useContext(AccordionContext);
        const decoratedOnClick = useAccordionButton(eventKey);
        const isCurrentEventKey = activeEventKey === eventKey;

        return (
            <Button
                className={`group-dropdown-button align-items-center d-flex justify-content-between`}
                style={{
                    cursor: canDropdown ? "default" : "pointer",
                    background: "none"
                }}
                onClick={decoratedOnClick}
            >
                <OverflowLabel 
                    isVisible
                    text={text} 
                    tooltipPlacement="top"
                    labelClassName="ms-1"
                    labelStyle={{
                        color: getMainColors().textColor,
                        cursor: canDropdown ? "default" : "pointer", 
                        flexGrow: 1
                    }}
                    tooltipStyle={{flexGrow: 1}} />
                <i style={{
                    fontSize: "1.5rem", 
                    lineHeight: "1.5rem", 
                    color: getMainColors().dark, 
                    visibility: canDropdown ? "hidden" : "visible"
                }} className={`mdi mdi-chevron-down ms-auto rotating-icon ${isCurrentEventKey && "flipped"}`} />
            </Button>
        );
    }

    const RenderPharmGroupDropdownItems: any = (items: StoreGroupTree[]) => {
        const [hoveredId, setHoveredId] = useState(-1);

        const RenderChildren: any = (items: StoreGroupTree[], level: number, isHovered: boolean) => {
            return (
                <>
                {items.map((i, index) => (
                <Accordion className="m-0 border border-0" defaultActiveKey={"0:0"} key={i.id + ":" + index} style={{width: "100%"}}>
                    <div className="d-flex" style={{lineHeight: "1.5rem"}}>
                        <div style={{paddingLeft: (1.5*level) + "rem"}} />
                        <div className="ps-1 group-dropdown-item" 
                            onMouseEnter={() => setHoveredId(i.id)} onMouseLeave={() => setHoveredId(-1)}
                            style={{
                                borderColor: getMainColors().lightBackground,
                                backgroundColor: isHovered ? getMainColors().light : (hoveredId === i.id ? getMainColors().lightBackground : "inherit"),
                                color: isHovered || hoveredId === i.id ? "white" : "inherit",
                            }}>
                            <Form.Check
                                type="radio"
                                className="vertical-align-middle"
                                value={i.id}
                                id="default-checkbox"
                                onChange={(e) => handleChange(e, i.name)}
                                checked={storeGroupId === i.id}
                            />
                            <AccordionDropdownButton canDropdown={i.children.length === 0} text={i.name} eventKey={level + ":" + index} />
                        </div>
                    </div>
                    {i.children.length > 0 ? <Accordion.Collapse eventKey={level + ":" + index} className="pe-0">
                        {RenderChildren(i.children, level+1, isHovered ? isHovered : (hoveredId === i.id))}
                    </Accordion.Collapse> : <></>}
                </Accordion>
            ))}</>);
        }

        return (
            <>{RenderChildren(items, 0)}</>
        );
    }

    const storeGroupMenu = (
        <>
            <Form.Group className="d-grid">
                <Form.Label className="form-label pb-1 font-weight-bold" style={{fontSize: "1rem"}}>Pharmacy Group</Form.Label>
                <div className="scrollable-container-thin store-group-menu">
                    {RenderPharmGroupDropdownItems(groups.tree)}
                </div>
            </Form.Group>
        </>
    );

    return (
        <Dropdown show={isOpen} autoClose="outside" onToggle={toggleDropdown}>
            <Dropdown.Toggle
                variant="link"
                id="dropdown-notification"
                as={Link}
                to="#"
                onClick={toggleDropdown}
                onMouseEnter={() => setHoveringToggle(true)}
                onMouseLeave={() => setHoveringToggle(false)}
                className="dropdown-toggle d-inline-flex align-items-center gap-1 arrow-none notification-list"
            >
                <i className="noti-icon">
                    <TooltipIcon icon="uil-store-alt" placement="right" classes={["noti-icon"]} />
                </i>
                <div className="text-muted pharmacy-group-dropdown-name">{global.storeName}</div>
                <ZIcon iconClassName={
                    classNames(
                        {
                            "grow": hoveringToggle,
                            "flipped": isOpen
                        }, 'rotating-icon')
                    } icon={ZIconType.ArrowDown} />
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-lg" align="end" style={{ width: "300px" }}>
                <div className="dropdown-item noti-title group-dropdown-item-no-bg-hover">
                    {storeGroupMenu}
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default PharmacyGroupDropdown;
